/**
 * Settings Page
 * Manage contract master data download and other settings
 */

import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import ShortcutSettings from '../components/Shortcuts/ShortcutSettings';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('contract');
  const [selectedExchange, setSelectedExchange] = useState('NSE');
  const [downloading, setDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const { theme, changeTheme, updateCustomColors } = useTheme();

  // Smart Order Engine settings state
  const [smartOrderConfig, setSmartOrderConfig] = useState({
    smartOrderEnabled:  true,
    enableMarketOrders: false,
    bufferPercent:      0.5,
    maxRetryCount:      2,
    retryDelayMs:       400
  });
  const [smartOrderLoading, setSmartOrderLoading] = useState(true);
  const [smartOrderSaving,  setSmartOrderSaving]  = useState(false);
  const [smartOrderStatus,  setSmartOrderStatus]  = useState(null);

  const tabs = [
    { id: 'contract', label: 'Contract Master', icon: '📊' },
    { id: 'themes',   label: 'Themes',          icon: '🎨' },
    { id: 'trading',  label: 'Trading',          icon: '⚡' },
    { id: 'shortcuts',label: 'Keyboard Shortcuts',icon: '⌨️' }
  ];

  const exchanges = [
    { value: 'NSE', label: 'NSE - National Stock Exchange' },
    { value: 'NFO', label: 'NFO - NSE Futures & Options' },
    { value: 'BSE', label: 'BSE - Bombay Stock Exchange' },
    { value: 'BFO', label: 'BFO - BSE Futures & Options' },
    { value: 'MCX', label: 'MCX - Multi Commodity Exchange' },
    { value: 'CDS', label: 'CDS - Currency Derivative Segment' },
    { value: 'INDICES', label: 'INDICES - Market Indices' }
  ];

  const predefinedThemes = [
    { name: 'classic', label: 'Classic', buy: '#10b981', sell: '#ef4444', preview: 'linear-gradient(135deg, #10b981, #ef4444)' },
    { name: 'ocean', label: 'Ocean Blue', buy: '#0ea5e9', sell: '#8b5cf6', preview: 'linear-gradient(135deg, #0ea5e9, #8b5cf6)' },
    { name: 'sunset', label: 'Sunset', buy: '#f59e0b', sell: '#ec4899', preview: 'linear-gradient(135deg, #f59e0b, #ec4899)' },
    { name: 'forest', label: 'Forest', buy: '#059669', sell: '#dc2626', preview: 'linear-gradient(135deg, #059669, #dc2626)' },
    { name: 'neon', label: 'Neon', buy: '#06d6a0', sell: '#f72585', preview: 'linear-gradient(135deg, #06d6a0, #f72585)' }
  ];

  const handleDownload = async () => {
    if (!selectedExchange) {
      setDownloadStatus({ success: false, message: 'Please select an exchange' });
      return;
    }

    setDownloading(true);
    setDownloadStatus(null);

    try {
      const response = await api.post(`/symbols/download/${selectedExchange}`);
      
      if (response.data.success) {
        setDownloadStatus({
          success: true,
          message: `Successfully downloaded ${response.data.data.count} symbols for ${selectedExchange}. Inserted: ${response.data.data.inserted}, Updated: ${response.data.data.updated}`
        });
      } else {
        setDownloadStatus({
          success: false,
          message: response.data.error || 'Failed to download symbols'
        });
      }
    } catch (error) {
      setDownloadStatus({
        success: false,
        message: error.response?.data?.error || error.message || 'Failed to download symbols'
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadAll = async () => {
    setDownloading(true);
    setDownloadStatus(null);

    try {
      const response = await api.post('/symbols/download-all');
      
      if (response.data.success) {
        const summary = response.data.data
          .map(r => `${r.exchange}: ${r.data?.count || 0} symbols`)
          .join(', ');
        
        setDownloadStatus({
          success: true,
          message: `Successfully downloaded all exchanges. ${summary}`
        });
      } else {
        setDownloadStatus({
          success: false,
          message: 'Failed to download symbols'
        });
      }
    } catch (error) {
      setDownloadStatus({
        success: false,
        message: error.response?.data?.error || error.message || 'Failed to download symbols'
      });
    } finally {
      setDownloading(false);
    }
  };

  // Load smart order config from backend on mount
  useEffect(() => {
    const fetchSmartOrderConfig = async () => {
      try {
        const res = await api.get('/settings/smart-order');
        if (res.data.success) setSmartOrderConfig(res.data.data);
      } catch (err) {
        console.warn('Could not load smart order settings:', err.message);
      } finally {
        setSmartOrderLoading(false);
      }
    };
    fetchSmartOrderConfig();
  }, []);

  const handleSmartOrderSave = async () => {
    setSmartOrderSaving(true);
    setSmartOrderStatus(null);
    try {
      const res = await api.post('/settings/smart-order', smartOrderConfig);
      if (res.data.success) {
        setSmartOrderStatus({ success: true, message: res.data.message });
      } else {
        setSmartOrderStatus({ success: false, message: res.data.error || 'Save failed' });
      }
    } catch (err) {
      setSmartOrderStatus({
        success: false,
        message: err.response?.data?.error || err.message || 'Save failed'
      });
    } finally {
      setSmartOrderSaving(false);
      setTimeout(() => setSmartOrderStatus(null), 4000);
    }
  };

  const handleThemeChange = (themeName) => {
    changeTheme(themeName);
  };

  const handleCustomColorChange = (type, color) => {
    updateCustomColors({
      ...theme.colors,
      [type]: color
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 p-1">
      <div className="max-w-7xl mx-auto space-y-2">
        {/* Header */}
        <div className="text-center py-1">
          <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-0">
            Settings
          </h1>
          <p className="text-xs text-gray-600">Manage your trading preferences and data</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-2">
          <nav className="flex justify-center">
            <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-2 sm:px-3 py-1.5 rounded-md font-medium text-xs sm:text-sm transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'
                  }`}
                >
                  <span className="text-base mr-1 sm:mr-2">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </nav>
        </div>

        {/* Contract Master Tab Content */}
        {activeTab === 'contract' && (
          <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-xl border border-white/20 p-3">
            <div className="flex items-center mb-2">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-2">
                <span className="text-base sm:text-lg">📊</span>
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-gray-800">Contract Master Data</h2>
                <p className="text-xs sm:text-sm text-gray-600">Download trading symbols and instrument data</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-gray-700 leading-relaxed">
                📈 Download contract master data from AliceBlue for symbol search and watchlist functionality.
                This data includes all trading symbols, instrument details, and exchange information.
              </p>
            </div>

            <div className="space-y-3">
              {/* Exchange Selector */}
              <div className="bg-white/50 rounded-lg p-2 border border-gray-200/50">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  🏢 Select Exchange
                </label>
                <select
                  value={selectedExchange}
                  onChange={(e) => setSelectedExchange(e.target.value)}
                  disabled={downloading}
                  className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-300 bg-white/80 backdrop-blur-sm text-sm"
                >
                  {exchanges.map((exchange) => (
                    <option key={exchange.value} value={exchange.value}>
                      {exchange.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Download Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold py-2 px-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
                >
                  {downloading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Downloading...
                    </>
                  ) : (
                    <>
                      📥 Download {selectedExchange} Data
                    </>
                  )}
                </button>

                <button
                  onClick={handleDownloadAll}
                  disabled={downloading}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-4 rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm"
                >
                  {downloading ? '⏳ Downloading...' : '📦 Download All Exchanges'}
                </button>
              </div>

              {/* Status Message */}
              {downloadStatus && (
                <div
                  className={`p-4 rounded-lg border-2 ${
                    downloadStatus.success
                      ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-800'
                      : 'bg-gradient-to-r from-red-50 to-rose-50 border-red-200 text-red-800'
                  } shadow-lg`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {downloadStatus.success ? (
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                          <svg className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="ml-3">
                      <h4 className="font-semibold mb-1">
                        {downloadStatus.success ? '✅ Success!' : '❌ Error!'}
                      </h4>
                      <p className="text-sm">{downloadStatus.message}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border border-blue-200 rounded-lg p-2 shadow-sm">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-2">
                    <h3 className="text-xs font-semibold text-blue-800 mb-1">💡 Important Information</h3>
                    <div className="text-xs text-blue-700 space-y-0.5">
                      <div className="flex items-center">
                        <span className="mr-2">⏱️</span>
                        Download may take a few minutes depending on the exchange size
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">📊</span>
                        NSE and NFO typically have the most symbols (~2000-3000 each)
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">💾</span>
                        Data will be stored in the database and can be used for symbol search
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">🔄</span>
                        You can update the data anytime by downloading again
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Themes Tab Content */}
        {activeTab === 'themes' && (
          <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-xl border border-white/20 p-3">
            <div className="flex items-center mb-2">
              <div className="w-7 h-7 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-2">
                <span className="text-lg">🎨</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-800">Trading Button Themes</h2>
                <p className="text-xs text-gray-600">Customize your trading experience with beautiful colors</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-2 mb-3">
              <p className="text-xs text-gray-700 leading-relaxed">
                🎯 Customize the colors of Buy/Sell buttons in the Quick Order Entry panel. Choose from preset themes or create your own custom colors.
              </p>
            </div>

            <div className="space-y-3">
              {/* Current Theme Display */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border-2 border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">🎨 Current Theme: {theme.name.charAt(0).toUpperCase() + theme.name.slice(1)}</h3>
                <div className="flex gap-6">
                  <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-md">
                    <div 
                      className="w-8 h-8 rounded-lg border-2 border-white shadow-lg"
                      style={{ backgroundColor: theme.colors.buy }}
                    ></div>
                    <span className="font-medium text-gray-700">Buy Button</span>
                  </div>
                  <div className="flex items-center gap-3 bg-white rounded-lg p-4 shadow-md">
                    <div 
                      className="w-8 h-8 rounded-lg border-2 border-white shadow-lg"
                      style={{ backgroundColor: theme.colors.sell }}
                    ></div>
                    <span className="font-medium text-gray-700">Sell Button</span>
                  </div>
                </div>
              </div>

              {/* Predefined Themes */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">🌈 Preset Themes</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {predefinedThemes.map((presetTheme) => (
                    <div
                      key={presetTheme.name}
                      className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
                        theme.name === presetTheme.name
                          ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-500/25'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-lg'
                      }`}
                      onClick={() => handleThemeChange(presetTheme.name)}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <span className="font-semibold text-gray-800">{presetTheme.label}</span>
                        {theme.name === presetTheme.name && (
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div 
                        className="w-full h-12 rounded-lg mb-4 shadow-inner"
                        style={{ background: presetTheme.preview }}
                      ></div>
                      <div className="flex gap-3 justify-center">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full border border-gray-300"
                            style={{ backgroundColor: presetTheme.buy }}
                          ></div>
                          <span className="text-xs text-gray-600">Buy</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full border border-gray-300"
                            style={{ backgroundColor: presetTheme.sell }}
                          ></div>
                          <span className="text-xs text-gray-600">Sell</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Custom Theme */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">🛠️ Custom Theme</h3>
                <div className="bg-white/50 border-2 border-gray-200 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-3">
                        💚 Buy Button Color
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={theme.colors.buy}
                          onChange={(e) => handleCustomColorChange('buy', e.target.value)}
                          className="w-12 h-10 border-2 border-gray-300 rounded-lg cursor-pointer shadow-md hover:shadow-lg transition-shadow"
                        />
                        <input
                          type="text"
                          value={theme.colors.buy}
                          onChange={(e) => handleCustomColorChange('buy', e.target.value)}
                          className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all duration-200 font-mono text-sm bg-white/80"
                          placeholder="#10b981"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">
                        ❤️ Sell Button Color
                      </label>
                      <div className="flex items-center gap-4">
                        <input
                          type="color"
                          value={theme.colors.sell}
                          onChange={(e) => handleCustomColorChange('sell', e.target.value)}
                          className="w-12 h-10 border-2 border-gray-300 rounded-lg cursor-pointer shadow-md hover:shadow-lg transition-shadow"
                        />
                        <input
                          type="text"
                          value={theme.colors.sell}
                          onChange={(e) => handleCustomColorChange('sell', e.target.value)}
                          className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 font-mono text-sm bg-white/80"
                          placeholder="#ef4444"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleThemeChange('custom')}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white font-semibold py-3 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    🎨 Apply Custom Theme
                  </button>
                </div>
              </div>

              {/* Preview Section */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border-2 border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">👀 Live Preview</h3>
                <div className="flex gap-4 justify-center">
                  <button 
                    className="px-8 py-4 text-white text-lg font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    style={{ backgroundColor: theme.colors.buy }}
                  >
                    💚 Buy
                  </button>
                  <button 
                    className="px-8 py-4 text-white text-lg font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    style={{ backgroundColor: theme.colors.sell }}
                  >
                    ❤️ Sell
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Trading Tab Content */}
        {activeTab === 'trading' && (
          <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-xl border border-white/20 p-3">
            {/* Header */}
            <div className="flex items-center mb-3">
              <div className="w-7 h-7 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-2">
                <span className="text-base">⚡</span>
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-800">Smart Order Engine</h2>
                <p className="text-xs text-gray-600">
                  Configure how S-MKT orders are converted to LIMIT orders
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-2 mb-4 text-xs text-gray-700 leading-relaxed">
              When <strong>S-MKT</strong> is selected, the backend converts the order to a
              LIMIT order at <strong>LTP ± buffer%</strong> to avoid broker rejection and
              reduce slippage. Use the controls below to tune the behaviour.
            </div>

            {smartOrderLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-400 text-sm">
                Loading settings…
              </div>
            ) : (
              <div className="space-y-4">

                {/* Enable / Disable toggles */}
                <div className="bg-white/60 border border-gray-200 rounded-lg p-4 space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Engine Switches</h3>

                  {/* Smart Order Enabled */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Smart Order Engine</p>
                      <p className="text-xs text-gray-500">
                        Convert S-MKT orders to LIMIT orders automatically
                      </p>
                    </div>
                    <button
                      onClick={() => setSmartOrderConfig(c => ({ ...c, smartOrderEnabled: !c.smartOrderEnabled }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                        smartOrderConfig.smartOrderEnabled ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          smartOrderConfig.smartOrderEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Allow raw MARKET */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Allow Raw MARKET Orders</p>
                      <p className="text-xs text-gray-500">
                        Pass S-MKT directly to broker without conversion (not recommended)
                      </p>
                    </div>
                    <button
                      onClick={() => setSmartOrderConfig(c => ({ ...c, enableMarketOrders: !c.enableMarketOrders }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                        smartOrderConfig.enableMarketOrders ? 'bg-red-500' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                          smartOrderConfig.enableMarketOrders ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                {/* Numeric params */}
                <div className="bg-white/60 border border-gray-200 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Price & Retry Parameters</h3>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                    {/* Buffer % */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Buffer % <span className="text-gray-400 font-normal">(0.05 – 10)</span>
                      </label>
                      <p className="text-[10px] text-gray-500 mb-2">
                        BUY: LTP × (1 + buffer%)<br/>
                        SELL: LTP × (1 − buffer%)
                      </p>
                      <input
                        type="number"
                        min="0.05"
                        max="10"
                        step="0.05"
                        value={smartOrderConfig.bufferPercent}
                        onChange={e => setSmartOrderConfig(c => ({ ...c, bufferPercent: parseFloat(e.target.value) || 0.5 }))}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none"
                      />
                      <p className="text-[10px] text-orange-600 mt-1 font-medium">
                        Example @ LTP 200: BUY → ₹{(200 * (1 + smartOrderConfig.bufferPercent / 100)).toFixed(2)}
                      </p>
                    </div>

                    {/* Max Retries */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Max Retries <span className="text-gray-400 font-normal">(0 – 10)</span>
                      </label>
                      <p className="text-[10px] text-gray-500 mb-2">
                        Times the price is modified if order is not filled
                      </p>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        step="1"
                        value={smartOrderConfig.maxRetryCount}
                        onChange={e => setSmartOrderConfig(c => ({ ...c, maxRetryCount: parseInt(e.target.value) || 0 }))}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none"
                      />
                    </div>

                    {/* Retry Delay */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">
                        Retry Delay (ms) <span className="text-gray-400 font-normal">(100 – 5000)</span>
                      </label>
                      <p className="text-[10px] text-gray-500 mb-2">
                        Wait time between each retry price check
                      </p>
                      <input
                        type="number"
                        min="100"
                        max="5000"
                        step="100"
                        value={smartOrderConfig.retryDelayMs}
                        onChange={e => setSmartOrderConfig(c => ({ ...c, retryDelayMs: parseInt(e.target.value) || 400 }))}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Live summary */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-3 border border-gray-200 text-xs text-gray-600 space-y-1">
                  <p className="font-semibold text-gray-700 mb-1">Current Config Summary</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
                    <span>Engine: <strong className={smartOrderConfig.smartOrderEnabled ? 'text-green-600' : 'text-red-500'}>{smartOrderConfig.smartOrderEnabled ? 'ON' : 'OFF'}</strong></span>
                    <span>Raw MARKET: <strong className={smartOrderConfig.enableMarketOrders ? 'text-red-500' : 'text-green-600'}>{smartOrderConfig.enableMarketOrders ? 'ALLOWED' : 'BLOCKED'}</strong></span>
                    <span>Buffer: <strong className="text-orange-600">{smartOrderConfig.bufferPercent}%</strong></span>
                    <span>Max Retries: <strong>{smartOrderConfig.maxRetryCount}</strong></span>
                    <span>Retry Delay: <strong>{smartOrderConfig.retryDelayMs} ms</strong></span>
                  </div>
                </div>

                {/* Status message */}
                {smartOrderStatus && (
                  <div className={`p-3 rounded-lg border text-sm font-medium ${
                    smartOrderStatus.success
                      ? 'bg-green-50 border-green-200 text-green-800'
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}>
                    {smartOrderStatus.success ? '✅ ' : '❌ '}{smartOrderStatus.message}
                  </div>
                )}

                {/* Save button */}
                <button
                  onClick={handleSmartOrderSave}
                  disabled={smartOrderSaving}
                  className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-white font-semibold py-2.5 px-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {smartOrderSaving ? '⏳ Saving…' : '💾 Save Smart Order Settings'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Keyboard Shortcuts Tab Content */}
        {activeTab === 'shortcuts' && (
          <ShortcutSettings />
        )}
      </div>
    </div>
  );
};

export default Settings;
